import * as React from "react";
import { Mutation } from "react-apollo";
import gql from "graphql-tag";
import Select from "react-select";
import { Row, Col, Button, Form, FormGroup, Label, Input } from "reactstrap";
import { formatDropdown } from "src/Utils/Helpers";
// GraphQL
import { createCompany, createUserCompany } from "../../graphql/mutations";
import {
  CreateCompanyMutation,
  CreateCompanyMutationVariables,
  Province,
  Industry,
  Suburb,
  CreateUserCompanyMutation,
  CreateUserCompanyMutationVariables
} from "../../API";

// Presentation/UI
import ErrorMessage from "../../Components/Styled/ErrorMessage";
import Loader from "../../Components/Loader";

// Utils
import { COMPANY_SIZE } from "../../Utils/Consts";

type SelectType = {
  value: string;
  label: string;
};

type SelectProvinceType = {
  value: Province;
  label: string;
};

type SelectSuburbType = {
  value: Suburb;
  label: string;
};

type SelectIndustryType = {
  value: Industry;
  label: string;
};

type State = {
  createdAt: string;
  name: string;
  emailAddress: string;
  contactNumber: string;
  address: string;
  suburb: Suburb | null;
  province: SelectProvinceType;
  companySize: SelectType;
  companyIndustry: SelectIndustryType;
  loading: boolean;
  error: string | null;
};

type Props = {
  userId: string;
  updateView(view: string): void;
};

class CompanySetupForm extends React.Component<Props, State> {
  state: State = {
    createdAt: "",
    name: "",
    emailAddress: "",
    contactNumber: "",
    address: "",
    suburb: null,
    province: { value: Province.GAUTENG, label: "" },
    companySize: { value: "", label: "" },
    companyIndustry: { value: Industry.INDUSTRY_ONE, label: "" },
    loading: false,
    error: null
  };

  timeoutId: number = 0;

  componentWillUnmount() {
    window.clearTimeout(this.timeoutId);
  }

  // Select industry
  selectIndustry = (companyIndustry: SelectIndustryType): void => {
    this.setState({ companyIndustry });
  };

  // Select company size
  selectCompanySize = (companySize: SelectType): void => {
    this.setState({ companySize });
  };

  // Select company province
  selectCompanyProvince = (province: SelectProvinceType): void => {
    this.setState({ province });
  };

  selectCompanySuburb = (suburb: SelectSuburbType): void => {
    this.setState({ suburb: suburb.value });
  };

  // Select company industry
  selectCompanyIndustry = (companyIndustry: SelectIndustryType): void => {
    this.setState({ companyIndustry });
  };

  // format constants into dropdowns for provinces and industries
  listOfProvinces = (consts: object) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  listOfSuburbs = () => {
    return Object.keys(Suburb).map((item: string) => {
      return formatDropdown(item);
    });
  };

  listOfIndustries = (consts: object) => {
    return Object.keys(consts).map((item: string) => {
      return formatDropdown(item);
    });
  };

  // Handle change in input fields
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "name":
        this.setState({ name: value });
        break;
      case "emailAddress":
        this.setState({ emailAddress: value });
        break;
      case "contactNumber":
        this.setState({ contactNumber: value });
        break;
      case "address":
        this.setState({ address: value });
        break;
      default:
        break;
    }
  };

  // Validation
  validateForm = (): boolean => {
    const {
      name,
      emailAddress,
      contactNumber,
      address,
      suburb,
      province,
      companySize,
      companyIndustry
    } = this.state;
    // Check for undefined or empty input fields
    if (
      !name ||
      !emailAddress ||
      !contactNumber ||
      !address ||
      !suburb ||
      !province ||
      !companySize ||
      !companyIndustry
    ) {
      this.setError("Please complete the form.");
      return false;
    }

    return true;
  };

  // Error
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        this.timeoutId = window.setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  render() {
    const {
      createdAt,
      name,
      emailAddress,
      contactNumber,
      address,
      suburb,
      province,
      companySize,
      companyIndustry
    } = this.state;

    const { updateView, userId } = this.props;

    return (
      <Mutation<CreateCompanyMutation, CreateCompanyMutationVariables>
        mutation={gql(createCompany)}
      >
        {(createCompanyMutation, response) => (
          <Mutation<
            CreateUserCompanyMutation,
            CreateUserCompanyMutationVariables
          >
            mutation={gql(createUserCompany)}
          >
            {createUserCompanyMutation => (
              <Form
                onSubmit={e => {
                  e.preventDefault();
                  if (this.validateForm()) {
                    createCompanyMutation({
                      variables: {
                        input: {
                          createdAt,
                          name,
                          emailAddress,
                          contactNumber,
                          address,
                          suburb,
                          province: province.value,
                          numberOfEmployees: companySize.value,
                          industry: companyIndustry.value
                        }
                      }
                    })
                      .then((res: any) => {
                        const { id } = res.data.createCompany;
                        // update user by adding them to newly created company
                        createUserCompanyMutation({
                          variables: {
                            input: {
                              userCompanyCompanyId: id,
                              userCompanyUserId: userId
                            }
                          }
                        })
                          .then((updateUserRes: any) => {
                            // Change view to success view
                            updateView("success");
                          })
                          .catch((updateUserErr: any) => {
                            this.setError(updateUserErr.message);
                          });
                      })
                      .catch((err: any) => {
                        this.setError(err.message);
                      });
                  }
                }}
              >
                <Row>
                  <Col xs={12} md={12} lg={12}>
                    <FormGroup>
                      <Label for="name">Company Name</Label>
                      <Input
                        type="text"
                        name="name"
                        id="name"
                        value={name}
                        placeholder="Company name"
                        onChange={this.handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} lg={6}>
                    <FormGroup>
                      <Label for="emailAddress">Company Email</Label>
                      <Input
                        type="email"
                        name="emailAddress"
                        id="emailAddress"
                        value={emailAddress}
                        placeholder="Company Email"
                        onChange={this.handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6} lg={6}>
                    <FormGroup>
                      <Label for="emailAddress">Company Phone</Label>
                      <Input
                        type="number"
                        name="contactNumber"
                        id="contactNumber"
                        value={contactNumber}
                        placeholder="Company Phone"
                        onChange={this.handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={12} lg={12}>
                    <FormGroup>
                      <Label for="address">Company Address</Label>
                      <Input
                        type="text"
                        name="address"
                        id="address"
                        value={address}
                        placeholder="Company address"
                        onChange={this.handleChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} lg={6}>
                    <FormGroup>
                      <Label>Suburb</Label>
                      <Select
                        options={this.listOfSuburbs()}
                        onChange={this.selectCompanySuburb}
                        value={formatDropdown(suburb)}
                        isSearchable={true}
                        placeholder=""
                        className="select-styling"
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6} lg={6}>
                    <FormGroup>
                      <Label>Province</Label>
                      <Select
                        options={this.listOfProvinces(Province)}
                        onChange={this.selectCompanyProvince}
                        value={province}
                        isSearchable={true}
                        placeholder=""
                        className="select-styling"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={6} lg={6}>
                    <FormGroup>
                      <Label>Company Size</Label>
                      <Select
                        onChange={this.selectCompanySize}
                        options={COMPANY_SIZE}
                        value={companySize}
                        isSearchable={true}
                        placeholder=""
                        className="select-styling"
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6} lg={6}>
                    <FormGroup>
                      <Label>Industry</Label>
                      <Select
                        options={this.listOfIndustries(Industry)}
                        onChange={this.selectCompanyIndustry}
                        value={companyIndustry}
                        isSearchable={true}
                        placeholder=""
                        className="select-styling"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <br />
                <br />
                <Button>
                  {!response.loading ? <span>Next</span> : <Loader />}
                </Button>
                {this.state.error && (
                  <ErrorMessage errorMessage={this.state.error} />
                )}
              </Form>
            )}
          </Mutation>
        )}
      </Mutation>
    );
  }
}

export default CompanySetupForm;



// WEBPACK FOOTER //
// ./src/Containers/CompanySetup/CompanySetupForm.tsx